import {
  Box,
  Container,
  Grid2 as Grid,
  CircularProgress,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import CustomersGrid from "../../admin/CustomersGrid";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { getCustomers } from "../../../store/actions/dataActions";
import EditCustomer from "../../admin/EditCustomer";
import {
  isAllowedSubAdminRoute,
  isSubAdminEmail,
} from "../../../functions/helperFunctions";

const Customers = () => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state?.[currentSession]?.user);
  const currentSessionState = useSelector((state) =>
    currentSession ? state?.sessions?.[currentSession] : null,
  );
  const { subAdmins } = useSelector((state) => state.nocData || {});
  const { subAdminDomains = [], subAdminPages = [] } = subAdmins || {};

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [customerSelectedRow, setCustomerSelectedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  const fetchDataRef = useRef();
  const isSessionValidated = currentSessionState?.isValidated === true;
  const currentEmail = currentUser?.user?.email;
  const isSubAdmin = isSubAdminEmail(currentEmail, subAdminDomains);
  const isAllowedSubAdmin = isAllowedSubAdminRoute({
    email: currentEmail,
    subAdminDomains,
    subAdminPages,
    path: location.pathname,
  });

  useEffect(() => {
    if (!currentSession || !isSessionValidated || !currentUser?.user) return;

    const isAdminUser =
      currentUser?.user?.role === "admin" && !!currentUser?.user?.admin;

    if (!isAdminUser && !isAllowedSubAdmin) {
      navigate("/login");
    }
  }, [
    currentSession,
    isSessionValidated,
    currentUser?.user,
    isAllowedSubAdmin,
    isSubAdmin,
    navigate,
  ]);

  useEffect(() => {
    if (!currentSession || !isSessionValidated || !currentUser?.user) {
      return;
    }

    const isAdminUser =
      currentUser?.user?.role === "admin" && !!currentUser?.user?.admin;

    if (!isAdminUser && !isAllowedSubAdmin) {
      return;
    }

    const fetchData = async () => {
      setDataLoading(true);

      let page = null;
      let rowsPerPage = null;
      let selectedCustomerId = null;

      try {
        const stored = sessionStorage.getItem("adminRemoteCustomersState");
        if (stored) {
          const parsed = JSON.parse(stored);
          page =
            typeof parsed.page === "number" && parsed.page >= 0
              ? parsed.page
              : null;
          rowsPerPage =
            typeof parsed.pageSize === "number" && parsed.pageSize > 0
              ? parsed.pageSize
              : null;
          selectedCustomerId = parsed.selectedCustomerId || null;
        }
      } catch (e) {
        // ignore parse errors
      }

      const resp = await dispatch(
        getCustomers({
          page: page,
          rowsPerPage: rowsPerPage,
        }),
      );

      if (resp) {
        // If we had a previously selected customer, restore it
        if (selectedCustomerId) {
          const restoredRow = resp.find((row) => row.id === selectedCustomerId);
          if (restoredRow) {
            setCustomerSelectedRow(restoredRow);
          }
        }

        // Clear the stored state after using it once
        try {
          sessionStorage.removeItem("adminRemoteCustomersState");
        } catch (e) {
          // ignore
        }

        setDataLoading(false);
        setLoading(false);
      }
    };

    if (!fetchDataRef?.current) {
      fetchDataRef.current = true;
      fetchData();
    }
  }, [
    currentSession,
    isSessionValidated,
    currentUser?.user,
    isAllowedSubAdmin,
    isSubAdmin,
    dispatch,
    setCustomerSelectedRow,
  ]);

  return (
    <>
      {loading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <CircularProgress color="warning" size="10rem" />
          </Box>
        </>
      ) : (
        <>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              py: mobileSize ? 0 : 1,
              backgroundColor: "#EEF2F6",
            }}
          >
            <Container maxWidth="xl" sx={{ px: mobileSize ? 0 : 2 }}>
              <Grid container spacing={3} sx={{ my: mobileSize ? 0 : 2 }}>
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <CustomersGrid
                    customerSelectedRow={customerSelectedRow}
                    setCustomerSelectedRow={setCustomerSelectedRow}
                    dataLoading={dataLoading}
                    setDataLoading={setDataLoading}
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* *********************** BOTTOM LINE ******************************************** */}
          {/* ******************************************************************************** */}
          <Drawer
            slotProps={{
              paper: {
                sx: {
                  width: "50vw",
                },
              },
            }}
            anchor="right"
            open={openDrawer}
            onClose={() => setOpenDrawer(false)}
          >
            <EditCustomer
              row={customerSelectedRow}
              setCustomerSelectedRow={setCustomerSelectedRow}
              setOpenDrawer={setOpenDrawer}
            />
          </Drawer>
        </>
      )}
    </>
  );
};

export default Customers;
